/**
 * The portal host's requests for the help assistant (plan-019 WAPP-HOST; ED-2; design §31.9.2, §31.0.3 D17): the grant,
 * whether help is on, the unread count. Everything the help code sends goes through here and the console gateway's
 * `help` slice (`../api/slice/help.ts`) — never from `./fill/**`, which sends nothing.
 *
 * Signed in, the grant is `POST /v1/help/grants`, same-origin through the portal's `/v1/` proxy with the auth_session
 * cookie (the api refuses an operator acting as the person). Signed out — the portal's way-in pages — the grant is the
 * anonymous one: a fresh challenge, solved here with WebCrypto (`@wetalk/help-widget/anonymous`, the widget's own copy of
 * the solver), the visitor key from `localStorage` (`wt-hp:k`, rotated after a day), and `POST /v1/public/help/grants`
 * with surface `portal`.
 */
import { queryOptions } from "@tanstack/react-query";
import type { HelpGrantAnswer, HelpLanguage } from "@wetalk/domain";
import { HELP_STORAGE_KEY } from "@wetalk/help-widget/path";
import {
  altchaPayloadOf,
  challengeOf,
  helpVisitorKeyOf,
  solveAltcha,
  type RandomFill,
  type SubtleLike,
} from "@wetalk/help-widget/anonymous";

import type { ConsoleGateway } from "../api/gateway.js";

/** How often the console reads the help status again while it is open (owner decision 2026-10-15). */
export const HELP_STATUS_REFRESH_MS = 60_000;

/**
 * Whether help is on and takes a new conversation now (`GET /v1/public/help/status`: the day's help token budget not
 * reached and a GPU pool able to take help, owner decision 2026-10-15): one cache entry for the mount, the person menu,
 * the widget's own re-check and `/help`. Read again every minute, and on window focus, so the launcher hides (and comes
 * back) within about a minute.
 */
export const helpStatusQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    queryKey: ["help", "status"] as const,
    queryFn: () => gateway.readHelpStatus(),
    // The api caches it for half a minute too.
    staleTime: 30_000,
    refetchInterval: HELP_STATUS_REFRESH_MS,
    retry: false,
  });

/** The panel was open in this tab (`wt-hp:o`, the widget's own mark) — a reload in the middle of a conversation. */
export function helpPanelOpenInTab(): boolean {
  try {
    return globalThis.sessionStorage.getItem(HELP_STORAGE_KEY.panelOpen) === "1";
  } catch {
    return false;
  }
}

/** Where the panel was opened: the registered route (or the page's route slug) and the screen's label. */
export interface HelpPlace {
  readonly routeId: string | null;
  readonly screenLabel: string | null;
}

/** What an anonymous grant or request needs from the browser; passed in so a suite drives it with fakes. */
export interface AnonymousHelpEnvironment {
  readonly subtle: SubtleLike;
  /** `localStorage`, as a getter: reading it throws where storage is blocked. */
  readonly storage: () => Storage;
  readonly random: RandomFill;
  readonly now: () => number;
}

export function browserAnonymousHelpEnvironment(): AnonymousHelpEnvironment {
  return {
    subtle: globalThis.crypto.subtle,
    storage: () => globalThis.localStorage,
    random: (bytes) => globalThis.crypto.getRandomValues(bytes),
    now: () => Date.now(),
  };
}

/** The challenge could not be read or solved: the grant or the request is not sent. */
export class HelpChallengeUnsolvedError extends Error {
  override readonly name = "HelpChallengeUnsolvedError";
}

/** A fresh challenge, solved: the `altcha` value of one anonymous grant or request (each needs its own). */
export async function solvedHelpChallenge(gateway: ConsoleGateway, environment: AnonymousHelpEnvironment): Promise<string> {
  const challenge = challengeOf(await gateway.readPublicHelpChallenge());
  if (challenge === null) throw new HelpChallengeUnsolvedError("The help challenge is not one this page can solve.");
  const solution = await solveAltcha(environment.subtle, challenge, () => false);
  if (solution === null) throw new HelpChallengeUnsolvedError("The help challenge has no solution.");
  return altchaPayloadOf(challenge, solution);
}

/** The visitor key of this browser (per-key abuse limits; design §31.8.10). */
export function helpVisitorKey(environment: AnonymousHelpEnvironment): string {
  return helpVisitorKeyOf(environment.storage, environment.random, environment.now());
}

/** A single-use grant: the signed-in person's, or an anonymous visitor's on a signed-out page. */
export async function helpGrantOf(
  gateway: ConsoleGateway,
  signedIn: boolean,
  language: HelpLanguage,
  place: HelpPlace,
  environment: AnonymousHelpEnvironment,
): Promise<HelpGrantAnswer> {
  if (signedIn) return gateway.createHelpGrant({ language, routeId: place.routeId, screenLabel: place.screenLabel });
  const altcha = await solvedHelpChallenge(gateway, environment);
  return gateway.createPublicHelpGrant({
    altcha,
    visitorKey: helpVisitorKey(environment),
    language,
    routeId: place.routeId,
    screenLabel: place.screenLabel,
  });
}

/** Unread replies in "Your messages" (design §31.0.3 D10): one cheap read of the first page's count. */
export async function helpUnreadCountOf(gateway: ConsoleGateway): Promise<number> {
  return (await gateway.listHelpRequests(null)).unreadCount;
}
